import React, { useState } from 'react';
import { X, Sparkles, RefreshCw, Check, Loader2 } from 'lucide-react';
import { GlassButton } from '../ui/GlassButton';
import {
  generateBrandLogoPreview,
  confirmBrandLogo,
  LogoPreviewResponse,
} from '../../services/api';
import './LogoStudioModal.css';

interface LogoStudioModalProps {
  brandGuideId: string;
  brandName: string;
  primaryColor: string;
  currentLogoUrl?: string;
  onClose: () => void;
  onLogoUpdated: (newLogoUrl: string) => void;
}

type DesignStyle = 'modern' | 'classic' | 'bold' | 'playful';
type Layout = 'icon_left' | 'icon_above' | 'icon_only' | 'wordmark_only';
type Shape = 'geometric' | 'organic';
type Complexity = 'combination' | 'minimal' | 'wordmark';

const STYLE_OPTIONS: { value: DesignStyle; label: string; sub: string }[] = [
  { value: 'modern', label: 'Modern Minimal', sub: 'Vercel, Blue Bottle' },
  { value: 'classic', label: 'Refined Classic', sub: 'Tiffany, Ralph Lauren' },
  { value: 'bold', label: 'Bold Geometric', sub: 'IBM, Mastercard' },
  { value: 'playful', label: 'Playful Hand-crafted', sub: 'Mailchimp, Headspace' },
];

const LAYOUT_OPTIONS: { value: Layout; label: string; sub: string }[] = [
  { value: 'icon_left', label: 'Icon left of wordmark', sub: 'AWS, Vercel' },
  { value: 'icon_above', label: 'Icon above wordmark', sub: 'Airbnb, Pinterest' },
  { value: 'icon_only', label: 'Icon only', sub: 'Apple, Nike' },
  { value: 'wordmark_only', label: 'Wordmark only', sub: 'Google, FedEx' },
];

const SHAPE_OPTIONS: { value: Shape; label: string }[] = [
  { value: 'geometric', label: 'Geometric' },
  { value: 'organic', label: 'Organic' },
];

const deriveComplexity = (layout: Layout): Complexity => {
  if (layout === 'icon_only') return 'minimal';
  if (layout === 'wordmark_only') return 'wordmark';
  return 'combination';
};

const LogoStudioModal: React.FC<LogoStudioModalProps> = ({
  brandGuideId,
  brandName,
  primaryColor,
  currentLogoUrl,
  onClose,
  onLogoUpdated,
}) => {
  const [designStyle, setDesignStyle] = useState<DesignStyle>('modern');
  const [layout, setLayout] = useState<Layout>('icon_left');
  const [shape, setShape] = useState<Shape>('geometric');
  const [complexity, setComplexity] = useState<Complexity | null>(null);
  const [iconHint, setIconHint] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState<LogoPreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setPreview(null);
    try {
      const finalComplexity = complexity || deriveComplexity(layout);
      const result = await generateBrandLogoPreview(brandGuideId, {
        brand_name: brandName,
        design_style: designStyle,
        complexity: finalComplexity,
        shape,
        layout,
        icon_hint: iconHint.trim() || undefined,
      });
      setPreview(result);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Logo generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setConfirming(true);
    setError(null);
    try {
      const result = await confirmBrandLogo(brandGuideId, preview.preview_id);
      if (result.logo_url) {
        onLogoUpdated(result.logo_url);
      }
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to commit logo. Please try again.');
      setConfirming(false);
    }
  };

  const generateLabel = preview ? 'Try again' : (generating ? 'Generating…' : 'Generate');

  return (
    <div className="logo-studio-backdrop" onClick={handleBackdropClick}>
      {/* stopPropagation prevents in-modal clicks (style pills, layout pills, etc.)
          from bubbling up to the PARENT BrandGuidePreview's overlay — which has
          onClick={onClose} and would otherwise close the parent on every click. */}
      <div className="logo-studio-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="logo-studio-header">
          <div className="logo-studio-title">
            <Sparkles size={20} />
            <h2>Generate Brand Logo</h2>
          </div>
          <div className="logo-studio-chips">
            <span className="logo-chip">{brandName}</span>
            <span className="logo-chip logo-chip-color" title={primaryColor}>
              <span
                className="logo-chip-swatch"
                style={{ backgroundColor: primaryColor }}
              />
              {primaryColor}
            </span>
          </div>
          <button
            type="button"
            className="logo-studio-close"
            aria-label="Close"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        <div className="logo-studio-body">
          {/* LEFT: Controls */}
          <div className="logo-studio-controls">
            <div className="control-group">
              <label className="control-label">Style</label>
              <div className="pill-grid">
                {STYLE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`pill ${designStyle === opt.value ? 'pill-active' : ''}`}
                    onClick={() => setDesignStyle(opt.value)}
                  >
                    <span className="pill-label">{opt.label}</span>
                    <span className="pill-sub">{opt.sub}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="control-group">
              <label className="control-label">Layout</label>
              <div className="pill-grid">
                {LAYOUT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`pill ${layout === opt.value ? 'pill-active' : ''}`}
                    onClick={() => {
                      setLayout(opt.value);
                      setComplexity(null); // reset override; auto-derive
                    }}
                  >
                    <span className="pill-label">{opt.label}</span>
                    <span className="pill-sub">{opt.sub}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="control-group">
              <label className="control-label" htmlFor="icon-hint-input">
                Icon hint <span className="control-optional">(optional)</span>
              </label>
              <input
                id="icon-hint-input"
                type="text"
                className="control-input"
                placeholder="e.g. coffee bean, leaf, wave"
                value={iconHint}
                onChange={(e) => setIconHint(e.target.value)}
                disabled={layout === 'wordmark_only'}
              />
              {layout === 'wordmark_only' && (
                <span className="control-help">Wordmark-only layout has no icon.</span>
              )}
            </div>

            <div className="control-group">
              <button
                type="button"
                className="advanced-toggle"
                onClick={() => setAdvancedOpen((s) => !s)}
              >
                Advanced {advancedOpen ? '−' : '+'}
              </button>
              {advancedOpen && (
                <div className="advanced-panel">
                  <div className="advanced-row">
                    <label className="control-label">Shape language</label>
                    <div className="pill-row">
                      {SHAPE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={`pill pill-sm ${shape === opt.value ? 'pill-active' : ''}`}
                          onClick={() => setShape(opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="advanced-row">
                    <label className="control-label">
                      Complexity <span className="control-optional">(auto: {deriveComplexity(layout)})</span>
                    </label>
                    <div className="pill-row">
                      {(['combination', 'minimal', 'wordmark'] as Complexity[]).map((c) => (
                        <button
                          key={c}
                          type="button"
                          className={`pill pill-sm ${(complexity || deriveComplexity(layout)) === c ? 'pill-active' : ''}`}
                          onClick={() => setComplexity(c)}
                        >
                          {c.charAt(0).toUpperCase() + c.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="control-actions">
              <GlassButton
                variant="primary"
                onClick={handleGenerate}
                disabled={generating || confirming}
                style={{ width: '100%' }}
              >
                {generating ? (
                  <><Loader2 size={16} className="spin" /> Generating…</>
                ) : preview ? (
                  <><RefreshCw size={16} /> Try again</>
                ) : (
                  <><Sparkles size={16} /> Generate</>
                )}
              </GlassButton>
              <GlassButton variant="secondary" onClick={onClose} disabled={generating || confirming} style={{ width: '100%' }}>
                Cancel
              </GlassButton>
            </div>

            {error && <div className="logo-studio-error">{error}</div>}
          </div>

          {/* RIGHT: Preview */}
          <div className="logo-studio-preview">
            {!preview && !generating && (
              <div className="preview-empty">
                {currentLogoUrl ? (
                  <>
                    <div className="preview-empty-label">Current logo</div>
                    <img src={currentLogoUrl} alt="Current logo" className="preview-empty-img" />
                    <p className="preview-empty-hint">
                      Pick a style and layout, then click <strong>Generate</strong> to see new variants.
                    </p>
                  </>
                ) : (
                  <p className="preview-empty-hint">
                    Pick a style and layout, then click <strong>Generate</strong>.
                  </p>
                )}
              </div>
            )}

            {generating && (
              <div className="preview-loading">
                <Loader2 size={32} className="spin" />
                <p>Generating logo variants… (≈15-25s)</p>
              </div>
            )}

            {preview && !generating && (
              <>
                <div className="variant-grid">
                  <div className="variant-card variant-on-white">
                    <div className="variant-label">Primary (on white)</div>
                    <div className="variant-canvas variant-canvas-white">
                      <img src={preview.variants.primary} alt="Primary logo" />
                    </div>
                  </div>
                  <div className="variant-card">
                    <div className="variant-label">On brand colour</div>
                    <div
                      className="variant-canvas"
                      style={{ backgroundColor: preview.primary_color || primaryColor }}
                    >
                      <img src={preview.variants.dark_bg} alt="Dark-bg logo" />
                    </div>
                  </div>
                  <div className="variant-card">
                    <div className="variant-label">Mono black</div>
                    <div className="variant-canvas variant-canvas-grey">
                      <img src={preview.variants.mono_black} alt="Mono black logo" />
                    </div>
                  </div>
                  <div className="variant-card">
                    <div className="variant-label">Mono white</div>
                    <div className="variant-canvas variant-canvas-dark">
                      <img src={preview.variants.mono_white} alt="Mono white logo" />
                    </div>
                  </div>
                </div>

                <div className="preview-actions">
                  <GlassButton
                    variant="primary"
                    onClick={handleConfirm}
                    disabled={confirming}
                  >
                    {confirming ? (
                      <><Loader2 size={16} className="spin" /> Saving…</>
                    ) : (
                      <><Check size={16} /> Use this logo</>
                    )}
                  </GlassButton>
                  <span className="preview-hint">
                    If quality looks off, click <strong>Try again</strong> — Gemini outputs vary between runs.
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoStudioModal;
